/**
 * File: apps/qbench-cp/reactapp/src/shared/constants/statuses.js
 *
 * Purpose: Defines order and order-request status enums and the preset status filter lists used
 *     on order list views.
 *
 * Scope: Order and order-request status constants and their associated list-page filter
 *     definitions in the customer portal.
 *
 * Tags: customer-portal
 *
 * Overview: Exports `ORDER_REQUEST_STATUS` and `ORDER_STATUS` enums covering the order lifecycle
 *     (draft, submitted, pending changes, approved, received, completed, cancelled).
 *     `ORDERS_FILTER_BY_STATUS` pairs each status with a display title for filter UI, while
 *     `ORDERS_STATE` defines named filter presets (all, unread, draft, submitted, pending
 *     changes, cancelled) each carrying the query params needed to fetch that bucket of orders.
 *
 * Exports: ORDER_REQUEST_STATUS, ORDER_STATUS, ORDERS_FILTER_BY_STATUS, ORDERS_STATE.
 */

export const ORDER_REQUEST_STATUS = {
	draft: 'DRAFT',
	submitted: 'SUBMITTED',
	pending_changes: 'PENDING CHANGES',
	approved: 'APPROVED',
	received: 'RECEIVED',
};

export const ORDER_STATUS = {
	completed: 'COMPLETED',
	cancelled: 'CANCELLED',
};

export const ORDERS_FILTER_BY_STATUS = [
	{
		name: 'COMPLETED',
		title: 'Completed',
	},
	{
		name: 'RECEIVED',
		title: 'Received (In Progress)',
	},
	{
		name: 'APPROVED',
		title: 'Approved (Awaiting Samples)',
	},
	{
		name: 'SUBMITTED',
		title: 'Awaiting Approval',
	},
	{
		name: 'PENDING CHANGES',
		title: 'Revision Required',
	},
	{
		name: 'CANCELLED',
		title: 'Cancelled',
	},
];

export const ORDERS_STATE = [
  {
    name: 'all',
    title: 'All Submitted',
    params: {
      statuses: Object.values(ORDER_STATUS),
      exclude_order_request_statuses: [
        ORDER_REQUEST_STATUS.draft,
        ORDER_REQUEST_STATUS.submitted,
      ],
    },
  },
  {
    name: 'unread',
    title: 'unread',
    params: {
      portal_read: false,
      exclude_statuses: [ORDER_STATUS.cancelled],
      exclude_order_request_statuses: [ORDER_REQUEST_STATUS.draft],
    },
  },
  {
    name: 'draft',
    title: 'draft',
    params: {
      order_request_statuses: [ORDER_REQUEST_STATUS.draft],
    },
  },
  {
    name: 'submitted',
    title: 'Awaiting Approval',
    params: {
      exclude_statuses: [ORDER_STATUS.completed, ORDER_STATUS.cancelled],
      exclude_order_request_statuses: Object.values(
        ORDER_REQUEST_STATUS
      ).filter((s) => s !== ORDER_REQUEST_STATUS.submitted),
    },
  },
  {
    name: 'pending changes',
    title: 'Revision Required',
    params: {
      order_request_statuses: [ORDER_REQUEST_STATUS.pending_changes],
    },
  },
  {
    name: 'cancelled',
    title: 'Cancelled',
    params: {
      statuses: [ORDER_STATUS.cancelled],
    },
  },
];
